import { type CanvasModeType } from '../../../types';
import { type TransformConfig } from './types';

/**
 * Store-relevant subset of each transform's config.
 *
 * This is kept separate from the full `transforms` map (which includes React
 * component references like `Toolbar`) so that `store.ts` — and by extension
 * the barrel export — can avoid pulling in heavy component dependencies
 * (e.g. `@air/feature-ag-ui-chat`).
 */
export const TRANSFORM_STORE_CONFIGS: Record<
  Exclude<CanvasModeType, 'default'>,
  Pick<TransformConfig, 'isolateCommandHistory' | 'usesBoundingBox'>
> = {
  adjustImage: { isolateCommandHistory: false, usesBoundingBox: false },
  cameraAngle: { isolateCommandHistory: false, usesBoundingBox: false },
  cameraMotion: { isolateCommandHistory: false, usesBoundingBox: false },
  changeLighting: { isolateCommandHistory: false, usesBoundingBox: false },
  // Isolates history because bounding box draw/resize creates intermediate undo entries.
  // initialBox covers the full image; showGuideGrid adds a rule-of-thirds overlay.
  // allowOutsideBounds lets the box exceed [0,1] so crop can also generate background past the edge.
  crop: {
    isolateCommandHistory: true,
    usesBoundingBox: {
      allowOutsideBounds: true,
      initialBox: { __unit: 'normalized', height: 1, width: 1, x: 0, y: 0 },
      showGuideGrid: true,
    },
  },
  editTextOcr: { isolateCommandHistory: false, usesBoundingBox: false },
  // allowOutsideBounds lets the box exceed [0,1] to define the extension area beyond the image.
  extendBackground: {
    isolateCommandHistory: false,
    usesBoundingBox: {
      allowOutsideBounds: true,
      initialBox: { __unit: 'normalized', height: 1, width: 1, x: 0, y: 0 },
    },
  },
  imageToVideo: { isolateCommandHistory: false, usesBoundingBox: false },
  removeBackground: { isolateCommandHistory: false, usesBoundingBox: false },
  // Empty object enables the overlay with no special options — user draws a box from scratch.
  // Isolates history because the draw interaction creates intermediate undo entries.
  removeObject: { isolateCommandHistory: true, usesBoundingBox: {} },
  rotateFlip: { isolateCommandHistory: false, usesBoundingBox: false },
  solidBackground: { isolateCommandHistory: false, usesBoundingBox: false },
  upscale: { isolateCommandHistory: false, usesBoundingBox: false },
  // Video transforms
  videoExtend: { isolateCommandHistory: false, usesBoundingBox: false },
};
